import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';
import Typography from '../Typography';
import LocationIcon from '../Icons/LocationIcon';
import CalendarSolidIcon from '../Icons/CalendarSolidIcon';
import LocationFilterModal from './LocationFilterModal';
import TimeFilterModal from './TimeFilterModal'; // Importar el nuevo componente
import Button from '../Button/Button';
import ChevronDownIcon from '../Icons/ChevronDownIcon';

const LocationTimeComboPicker = ({
  toggleLocationFields,
  locationRef,
  municipality,
  filters,
  handleFilterChange,
  tFilter,
  // Props necesarias para el modal
  showLocationFields,
  tempAddress,
  setTempAddress,
  tempRadius,
  setTempRadius,
  handleApply,
  handleCancel,
  autocompleteRef,
  inputRef,
  handlePlaceSelect,
  minRadius,
  maxRadius,
  dropdownRef
}) => {
  // Estado para controlar la visibilidad del dropdown de tiempo
  const [showTimeFields, setShowTimeFields] = useState(false);
  // Estado temporal para el filtro de tiempo
  const [tempTimeFilter, setTempTimeFilter] = useState(filters.timeFilter || 'flexible');
  // Referencia para el dropdown de tiempo
  const timeDropdownRef = useRef(null);
  // Referencia para el botón de tiempo
  const timeRef = useRef(null);

  // Función para mostrar/ocultar el dropdown de tiempo
  const toggleTimeFields = () => {
    setShowTimeFields(!showTimeFields);
    setTempTimeFilter(filters.timeFilter || 'flexible');
  };

  // Función para aplicar el filtro de tiempo
  const handleApplyTime = (e) => {
    e.preventDefault();
    handleFilterChange('timeFilter', tempTimeFilter);
    setShowTimeFields(false);
  };

  // Función para cancelar los cambios
  const handleCancelTime = (e) => {
    e.preventDefault();
    setTempTimeFilter(filters.timeFilter || 'flexible');
    setShowTimeFields(false);
  };

  // Efecto para cerrar el dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        timeDropdownRef.current &&
        !timeDropdownRef.current.contains(event.target) &&
        timeRef.current &&
        !timeRef.current.contains(event.target) &&
        !event.target.closest('#applyTimeButton') &&
        !event.target.closest('#cancelTimeButton')
      ) {
        setShowTimeFields(false);
      }
    };

    if (showTimeFields) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showTimeFields]);

  return (
    <Filter>
      <Typography as="h2" $variant="title-5-semibold">
        {tFilter('locationFilters.header.title')}
      </Typography>

      <LocationTimeFilter>
        {/* locationFilters */}
        <LocationButton
          type="button"
          onClick={toggleLocationFields}
          ref={locationRef}
          $isActive={showLocationFields}
        >
          <LocationIcon fill={showLocationFields ? theme.colors.brandMain : theme.colors.defaultWeak} height={16} width={16} />
          <Typography
            as="p"
            $variant="title-5-semibold"
            color={showLocationFields ? theme.colors.brandMain : theme.colors.defaultWeak}
          >
            {municipality ? municipality : tFilter('locationFilters.location.loading')}
          </Typography>
          <ChevronIconWrapper $isRotated={showLocationFields}>
            <ChevronDownIcon fill={showLocationFields ? theme.colors.brandMain : theme.colors.defaultWeak} height={20} width={20} />
          </ChevronIconWrapper>
        </LocationButton>

        {showLocationFields && (
          <LocationFilterModal
            tempAddress={tempAddress}
            setTempAddress={setTempAddress}
            tempRadius={tempRadius}
            setTempRadius={setTempRadius}
            handleApply={handleApply}
            handleCancel={handleCancel}
            autocompleteRef={autocompleteRef}
            inputRef={inputRef}
            handlePlaceSelect={handlePlaceSelect}
            minRadius={minRadius}
            maxRadius={maxRadius}
            tFilter={tFilter}
            dropdownRef={dropdownRef}
          />
        )}

        {/* timeFilters - reemplazando el select por un botón que muestra un dropdown */}
        <TimeButton
          type="button"
          onClick={toggleTimeFields}
          ref={timeRef}
          $isActive={showTimeFields}
        >
          <CalendarSolidIcon fill={showTimeFields ? theme.colors.brandMain : theme.colors.defaultWeak} height={16} width={16} />
          <Typography
            as="p"
            $variant="title-5-semibold"
            color={showTimeFields ? theme.colors.brandMain : theme.colors.defaultWeak}
          >
            {tFilter(`timeFilters.time.${filters.timeFilter || 'flexible'}`)}
          </Typography>
          <ChevronIconWrapper $isRotated={showTimeFields}>
            <ChevronDownIcon fill={showTimeFields ? theme.colors.brandMain : theme.colors.defaultWeak} height={20} width={20} />
          </ChevronIconWrapper>
        </TimeButton>

        {/* Reemplazar el modal de tiempo con el nuevo componente */}
        {showTimeFields && (
          <TimeFilterModal
            tempTimeFilter={tempTimeFilter}
            setTempTimeFilter={setTempTimeFilter}
            handleApplyTime={handleApplyTime}
            handleCancelTime={handleCancelTime}
            tFilter={tFilter}
            timeDropdownRef={timeDropdownRef}
          />
        )}
      </LocationTimeFilter>
    </Filter>
  );
};

export default LocationTimeComboPicker;

// Mantener los estilos necesarios
const Filter = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: ${({ theme }) => theme.sizing.sm};
`;

const LocationTimeFilter = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${({ theme }) => theme.sizing.xxs};
  gap: ${({ theme }) => theme.sizing.xxs};
  // border-radius: ${({ theme }) => theme.radius.sm};
  border-radius: 12px;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  box-shadow: 0px 7px 4px -4px rgba(0, 0, 0, 0.08), 0px 0px 4px 0px rgba(0, 0, 0, 0.16);
`;

const FilterButton = styled.button`
  border-radius: ${({ theme }) => theme.radius.xs};
  padding: 0px ${({ theme }) => theme.sizing.xs};
  height: ${({ theme }) => theme.sizing.lg};
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
  position: relative;
  transition: all 0.3s;
  border: none;
  background-color: ${({ $isActive, theme }) =>
    $isActive ? theme.fill.brandAlphaMain16 : theme.fill.defaultMain};

  &:hover {
    background-color: ${({ $isActive, theme }) =>
    $isActive ? theme.fill.brandAlphaMain16 : theme.fill.defaultWeak};
  }
`;

// Nuevo componente para envolver el icono y permitir la rotación
const ChevronIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  transform: ${({ $isRotated }) => $isRotated ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const TimeButton = styled(FilterButton)``;

const LocationButton = styled(FilterButton)``;